import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  DecisionIQMutation,
  DecisionIQSurveyQuestionDto,
  DecisionIQSurveySubmissionDto,
  useDecisionIQSurveyQuery,
} from 'enrollment/queries';
import { Loader } from 'theSource/components';
import { PsCheckboxTuner } from 'stencilSetup';
import { SubscriberFieldsDto } from 'theSource/types';
import { DependentsResponseDto } from 'theSource/features/FamilyMembers/types';
import { PrescriptionSelect } from '.';
import { ChronicIllnessQuestions } from './ChronicIllnessQuestions';
import {
  addPrescriptionValues,
  collectDocFrequencyValues,
  initPrescriptionValues,
  insertOrRemoveValues,
} from '../..';
import { useConfigurationQuery } from 'theSource/queries';
import { selectEnrollmentContext } from 'theSource/features/Home/components/EnrollmentBanner/services';
import { OrbitDrawer } from 'enrollment/components/Shared/OrbitDrawer';
import { PSDialog } from 'enrollment/utils/common/Utils';

export interface DecisionIQMedicalSurveyProps {
  orgBenefitId: number;
  subscriber?: SubscriberFieldsDto;
  dependents?: DependentsResponseDto;
  standalonePage?: boolean;
  isModalSurvey?: boolean;
  drawerOpen?: boolean;
  onSubmit?: () => void;
  onRecommendationSurveySubmit?: (arg0: DecisionIQSurveySubmissionDto) => void;
}

export const DecisionIQMedicalSurvey = ({
  orgBenefitId,
  subscriber,
  dependents,
  standalonePage = false,
  isModalSurvey = false,
  drawerOpen = false,
  onSubmit,
  onRecommendationSurveySubmit,
}: DecisionIQMedicalSurveyProps) => {
  const { t } = useTranslation('pages.decisionIQ');

  const [isPrescriptionSelectDrawerOpen, setIsPrescriptionSelectDrawerOpen] =
    useState(false);
  const [isChronicConditionsDrawerOpen, setIsChronicConditionsDrawerOpen] =
    useState(false);

  const enrollmentContext = useSelector(selectEnrollmentContext);
  const [questionResponses, setQuestionResponses] = useState<
    DecisionIQSurveyQuestionDto[]
  >([]);
  const [coveredDependents, setCoveredDependents] = useState<
    DecisionIQSurveyQuestionDto[]
  >([]);
  const [pregnancyChecked, setPregnancyChecked] = useState(false);
  const [showPrescriptionDeps, setShowPrescriptionDeps] = useState(false);
  const [showPrescriptionSearch, setShowPrescriptionSearch] =
    useState<boolean[]>();
  const [showChronicDeps, setShowChronicDeps] = useState<boolean[]>();
  const [showChronicIllnesses, setShowChronicIllnesses] = useState(false);
  const [dependentPrescriptions, setDependentPrescriptions] = useState<{
    [key: number]: string[];
  }>({});
  const [subscriberPrescriptions, setSubscriberPrescriptions] = useState<
    string[]
  >([]);
  const familyLength = dependents ? dependents?.data.dependents.length + 1 : 1;
  const dependentIds = dependents
    ? dependents.data.dependents.map((dependent) => dependent.id)
    : [];

  const { configuration } = useConfigurationQuery();
  const {
    decisionIQSurveyData,
    isFetchingDecisionIQSurveys,
    isLoadingDecisionIQSurveys,
  } = useDecisionIQSurveyQuery({
    orgBenefitId: orgBenefitId,
    enabled: orgBenefitId > 0,
  });

  const questionTexts = [
    t('planning_a_pregnancy'),
    t('prescription_drugs'),
    t('chronic_conditions'),
  ];
  const questionCodes = [
    'medical_pregnancy',
    'prescriptiondrugs',
    'medical_chronic_conditions',
  ];

  const handleSubmitIntermediary = () => {
    const submitBtn = document.getElementById(
      'deciq-med-submit'
    ) as HTMLElement;

    if (isModalSurvey) {
      setTimeout(() => {
        submitBtn.click();
      }, 750);
    } else {
      submitBtn.click();
    }
  };

  const updateFormValues = (value: string) => {
    // [0]: questionCode shorthand (i.e. chronic, pregnancy, prescription),
    // [1]: option.value || checkbox (if unchecking question), [2]: dependentId
    // NOTE: pregnancy and prescription do not have [1] as option value since their
    // answers aren't based off the options.map
    const keyValues = value.split('-');
    let qIndex = 0;
    if (questionResponses) {
      const newQuestionResponses = [...questionResponses];

      // user has unchecked question, we need to set is_checked? to []
      if (keyValues[1] === 'checkbox') {
        qIndex = questionResponses.findIndex((question) => {
          return question.question_code === questionCodes[2];
        });

        newQuestionResponses[qIndex].options[Number(keyValues[2])][
          'is_checked?'
        ] = [];
        setQuestionResponses(newQuestionResponses);
        return;
      }

      switch (keyValues[0]) {
        case 'medical_pregnancy':
          qIndex = questionResponses.findIndex((question) => {
            return question.question_code === 'medical_pregnancy';
          });
          newQuestionResponses[qIndex].options[0].value =
            newQuestionResponses[qIndex].options[0].value === 0 ? 1 : 0;
          break;
        case 'prescription':
          qIndex = questionResponses.findIndex((question) => {
            return question.question_code === questionCodes[1];
          });
          break;
        case 'chronic':
          qIndex = questionResponses.findIndex((question) => {
            return question.question_code === questionCodes[2];
          });
          const oIndex = questionResponses[qIndex].options.findIndex(
            (option) => {
              return option.value === keyValues[1];
            }
          );

          // we need to essentially push or pop values from the is_checked array
          const oldIsCheckedArr =
            questionResponses[qIndex].options[oIndex]['is_checked?'];
          newQuestionResponses[qIndex].options[oIndex]['is_checked?'] =
            insertOrRemoveValues(keyValues[2], oldIsCheckedArr);
          setQuestionResponses(newQuestionResponses);
          break;
        default:
          break;
      }
    }
  };

  // Planning a Pregnancy checkbox
  const handlePlanPregnancy = (e: any) => {
    const target = e.detail;

    if (target?.checked != null && target?.name != null) {
      if (target.checked) {
        setPregnancyChecked(true);
        updateFormValues(target.name);
      } else {
        setPregnancyChecked(false);
        updateFormValues(target.name);
      }
    }
  };

  // Recurring Prescriptions checkbox
  const handleRecurringPrescriptions = (e: any) => {
    if (e.target.tagName !== 'INPUT') {
      return;
    }
    const targetChecked = e.target.checked;

    if (isMobileView) {
      setIsPrescriptionSelectDrawerOpen(true);
    } else {
      if (targetChecked) {
        setShowPrescriptionDeps(true);
      } else {
        setShowPrescriptionDeps(false);
      }
    }
  };

  const closeRecurringPrescriptionsDrawer = () => {
    PSDialog.dialogClose('prescriptionSelectDrawer', () =>
      setIsPrescriptionSelectDrawerOpen(false)
    );
  };

  // Chronic Conditions checkbox
  const handleChronicConditions = (e: any) => {
    if (e.target.tagName !== 'INPUT') {
      return;
    }
    const targetChecked = e.target.checked;

    if (isMobileView) {
      setIsChronicConditionsDrawerOpen(true);
    } else {
      if (targetChecked) {
        setShowChronicIllnesses(true);
      } else {
        setShowChronicIllnesses(false);
      }
    }
  };

  const closeChronicConditionsDrawer = () => {
    PSDialog.dialogClose('chronicIllnessQuestionsDrawer', () =>
      setIsChronicConditionsDrawerOpen(false)
    );
  };

  const { SurveyResponsesRequest } = DecisionIQMutation(
    orgBenefitId,
    String(configuration?.apiHost)
  );

  const handleSubmit = () => {
    const additionalQuestions = coveredDependents;

    additionalQuestions[1] = collectDocFrequencyValues(
      additionalQuestions[1],
      dependentIds
    );

    questionResponses[1] = addPrescriptionValues(
      questionResponses[1],
      subscriberPrescriptions,
      dependentPrescriptions
    );

    if (onRecommendationSurveySubmit) {
      const data = {
        dependent_ids: dependents
          ? dependents.data.dependents.map((dependent) => dependent.id)
          : undefined,
        questions: [...additionalQuestions, ...questionResponses],
        life_event_completed: false,
        enrollment_context_type: enrollmentContext,
      };
      onRecommendationSurveySubmit(data);
    } else {
      const data = {
        dependent_ids: dependents
          ? dependents.data.dependents.map((dependent) => dependent.id)
          : undefined,
        questions: [...additionalQuestions, ...questionResponses],
        life_event_completed: false,
        enrollment_context_type: enrollmentContext,
      };
      SurveyResponsesRequest(data);
    }

    if (onSubmit) {
      onSubmit();
    }
  };

  // this is used to initialize the state of the form - yes it is hard to read
  useEffect(() => {
    if (decisionIQSurveyData) {
      setQuestionResponses(decisionIQSurveyData.data.questions.slice(2));

      const responses = decisionIQSurveyData.data.questions;

      const pregnancyQs = responses.find(
        (response) => response.question_code === 'medical_pregnancy'
      );
      if (pregnancyQs && pregnancyQs.options[0].value === 1) {
        setPregnancyChecked(true);
      }
      const prescriptionQs = responses.find(
        (response) => response.question_code === questionCodes[1]
      );
      const initArray = new Array(familyLength).fill(false);
      const dependentPrescriptions: { [key: number]: string[] } = {};
      dependentIds.forEach((depId) => {
        dependentPrescriptions[depId] = new Array<string>();
      });
      initPrescriptionValues(
        dependentIds,
        prescriptionQs,
        initArray,
        setSubscriberPrescriptions,
        setDependentPrescriptions,
        setShowPrescriptionDeps,
        setShowPrescriptionSearch
      );

      const chronicQs = responses.find(
        (response) => response.question_code === questionCodes[2]
      );
      setShowChronicDeps(
        chronicQs?.options.map((option) =>
          Boolean(option['is_checked?'].length > 0)
        )
      );
      setShowChronicIllnesses(
        Boolean(
          chronicQs?.options.some((option) => option['is_checked?'].length > 0)
        )
      );
      setCoveredDependents(decisionIQSurveyData.data.questions.slice(0, 2));
    }
    // need to disable; updating state and then using a local varibale was causing endless re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decisionIQSurveyData]);

  useEffect(() => {
    const attachSubmitListener = (submitButton: HTMLElement | null) => {
      if (
        (standalonePage || isModalSurvey) &&
        !submitButton?.hasAttribute('listening-med')
      ) {
        submitButton?.addEventListener('click', handleSubmitIntermediary);
        submitButton?.setAttribute('listening-med', '');
      }
    };

    let submitButton;

    if (isModalSurvey) {
      setTimeout(() => {
        submitButton = document.getElementById('decisionIQSurveyDrawer_submit');
        attachSubmitListener(submitButton);
      }, 500);
    } else {
      submitButton = document.getElementById('enrollment-continue-button');
      attachSubmitListener(submitButton);
    }

    // we need to ensure we are only attaching the submit listener ONCE
  });

  useEffect(() => {
    if (decisionIQSurveyData && !drawerOpen) {
      const prescriptionQs = decisionIQSurveyData.data.questions.find(
        (response) => response.question_code === questionCodes[1]
      );
      const initArray = new Array(familyLength).fill(false);

      initPrescriptionValues(
        dependentIds,
        prescriptionQs,
        initArray,
        setSubscriberPrescriptions,
        setDependentPrescriptions,
        setShowPrescriptionDeps,
        setShowPrescriptionSearch
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen, decisionIQSurveyData]);

  if (isFetchingDecisionIQSurveys || isLoadingDecisionIQSurveys) {
    return <Loader />;
  }

  if (
    !decisionIQSurveyData ||
    decisionIQSurveyData.data.questions.length === undefined
  ) {
    return <></>;
  }

  const isMobileView = window.innerWidth < 744;
  const arePrescriptionsChecked =
    Object.values(dependentPrescriptions).some(
      (prescriptions) => prescriptions.length > 0
    ) || subscriberPrescriptions.length > 0;

  const prescriptionProps = {
    showPrescriptionSearch,
    setShowPrescriptionSearch,
    subscriber,
    dependents,
    subscriberPrescriptions,
    setSubscriberPrescriptions,
    dependentPrescriptions,
    setDependentPrescriptions,
  };

  const chronicIllnessProps = {
    showChronicDeps,
    setShowChronicDeps,
    subscriber,
    dependents,
    onDependentSelect: updateFormValues,
    questions: decisionIQSurveyData.data.questions.find(
      (response) => response.question_code === questionCodes[2]
    ) as DecisionIQSurveyQuestionDto,
  };

  return (
    <fieldset
      className="row mb-4 mt-2 pt-4 border-top"
      data-testid="decision-iq-med-survey"
    >
      <div className="col">
        <legend className="body1 mb-0">{t('additional_expenses')}</legend>
        <PsCheckboxTuner
          data-testid="pregnancy-checkbox"
          inputId="medical_pregnancy"
          name="medical_pregnancy"
          label={t('planning_a_pregnancy')}
          onPsChange={handlePlanPregnancy}
          checked={pregnancyChecked}
        />

        <PsCheckboxTuner
          data-testid="prescriptions-checkbox"
          inputId={questionCodes[1]}
          name={questionCodes[1]}
          label={questionTexts[1]}
          onClick={handleRecurringPrescriptions}
          checked={showPrescriptionDeps}
          labelClass={arePrescriptionsChecked ? 'checked' : ''}
        />
        {isMobileView ? (
          <OrbitDrawer
            drawerId="prescriptionSelectDrawer"
            heading="Recurring Prescriptions"
            isOpen={isPrescriptionSelectDrawerOpen}
            handleClose={closeRecurringPrescriptionsDrawer}
            footer={true}
            saveButton={t('common:save')}
            handleSubmit={closeRecurringPrescriptionsDrawer}
          >
            <PrescriptionSelect {...prescriptionProps} />
          </OrbitDrawer>
        ) : (
          showPrescriptionDeps && (
            <div className="ml-5">
              <PrescriptionSelect {...prescriptionProps} />
            </div>
          )
        )}

        <PsCheckboxTuner
          inputId={questionCodes[2]}
          name={questionCodes[2]}
          label={questionTexts[2]}
          onClick={handleChronicConditions}
          checked={showChronicIllnesses}
          labelClass={
            showChronicDeps && showChronicDeps.some((dep) => dep)
              ? 'checked'
              : ''
          }
        />
        {isMobileView ? (
          <OrbitDrawer
            drawerId="chronicIllnessQuestionsDrawer"
            heading="Chronic Conditions"
            isOpen={isChronicConditionsDrawerOpen}
            handleClose={closeChronicConditionsDrawer}
            footer={true}
            saveButton={t('common:save')}
            handleSubmit={closeChronicConditionsDrawer}
          >
            <ChronicIllnessQuestions {...chronicIllnessProps} />
          </OrbitDrawer>
        ) : (
          showChronicIllnesses && (
            <div className="ml-5">
              <ChronicIllnessQuestions {...chronicIllnessProps} />
            </div>
          )
        )}
        <button
          id="deciq-med-submit"
          onClick={() => handleSubmit()}
          style={{ display: 'none' }}
        ></button>
      </div>
    </fieldset>
  );
};
